/**

 * SquadMobileView – Player Team View (Mobile)

 * 

 * "This is my team" – not "This is a system"

 * 

 * Players first. Leaders below (collapsible).

 * Clean, social, team-oriented.

 */



import React, { useState, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';

import { useTeamMembers } from '../../hooks/useTeamMembers';

import { getInitials, cn } from '../../lib/utils';

import { Button } from '../ui/button';

import { Input } from '../ui/input';

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';

import { Skeleton } from '../ui/skeleton';

import {

  Users, Search, ArrowUpDown, Phone, Mail, MessageCircle,

  Shield, X, Check, ChevronRight,

} from 'lucide-react';



const POSITION_MAP = {

  'Målvakt':     { short: 'MV', color: '#EAB308', bg: 'rgba(234,179,8,0.12)' },

  'Back':        { short: 'B',  color: '#3B82F6', bg: 'rgba(59,130,246,0.12)' },

  'Mittfält':    { short: 'MF', color: '#22C55E', bg: 'rgba(34,197,94,0.12)' },

  'Anfall':      { short: 'FW', color: '#EF4444', bg: 'rgba(239,68,68,0.12)' },

  // Compatibility aliases for older stored labels.
  'Mittfältare': { short: 'MF', color: '#22C55E', bg: 'rgba(34,197,94,0.12)' },

  'Forward':     { short: 'FW', color: '#EF4444', bg: 'rgba(239,68,68,0.12)' },

};

const POSITION_SORT_ORDER = {
  'Målvakt': 1,
  'Back': 2,
  'Mittfält': 3,
  'Mittfältare': 3,
  'Anfall': 4,
  'Forward': 4,
};



const SORT_OPTIONS = [

  { value: 'name',     key: 'roster.sort_name' },

  { value: 'position', key: 'roster.sort_position' },

  { value: 'number',   key: 'roster.sort_jersey' },

];



const LEADER_ROLES = new Set([

  'coach', 'head_coach', 'assistant_coach', 'team_admin', 'admin', 'team_manager',

  'goalkeeper_coach', 'fitness_coach', 'analyst', 'leader', 'staff',

]);



function getRoleLabelKey(role) {

  const known = ['head_coach','assistant_coach','coach','team_admin','team_manager','admin',

    'goalkeeper_coach','fitness_coach','analyst','player','parent'];

  return known.includes(role) ? `roles.${role}` : 'roles.coach';

}



// ── Avatar ───────────────────────────────────────────

function MemberAvatar({ name, isLeader, size = 'md' }) {

  const cls = size === 'lg' ? 'h-14 w-14 text-base' : 'h-11 w-11 text-[13px]';

  return (

    <div

      className={`${cls} rounded-full flex items-center justify-center font-semibold shrink-0`}

      style={{

        background: isLeader

          ? "rgb(var(--tn-tint-ffffff) / 0.07)"

          : "rgb(var(--tn-tint-ffffff) / 0.06)",

        color: "var(--tn-readable-ffffff, rgb(var(--tn-text-ffffff) / 0.7))",

        border: "1.5px solid rgb(var(--tn-border-ffffff) / 0.08)",

      }}

    >

      {getInitials(name || 'U')}

    </div>

  );

}



// ── Player row ───────────────────────────────────────

function PlayerRow({ member, onClick, isLeader, t }) {

  // Subtitle:

  //  Player: "Spelare · #N · Position"  (omit pieces that are missing)

  //  Leader: role label

  const subtitle = isLeader

    ? t(getRoleLabelKey(member.role))

    : [t('roles.player'),

       member.jersey_number ? `#${member.jersey_number}` : null,

       member.position || null,

      ].filter(Boolean).join(' · ');



  return (

    <button

      onClick={() => onClick(member)}

      className="w-full text-left group"

      data-testid={`player-row-${member.id}`}

    >

      <div className="flex items-center gap-3 px-3.5 py-3 rounded-2xl transition-colors group-active:bg-[rgb(var(--tn-tint-ffffff))]/[0.04]"

        style={{

          background: "rgb(var(--tn-tint-ffffff) / 0.025)",

          border: "1px solid rgb(var(--tn-border-ffffff) / 0.05)",

        }}

      >

        <MemberAvatar name={member.name} isLeader={isLeader} />



        <div className="flex-1 min-w-0">

          <div className="flex items-center gap-1.5">

            <span className="text-[15px] font-medium text-[rgb(var(--tn-text-ffffff))] break-words leading-snug">

              {member.name}

            </span>

            {isLeader && <Shield className="w-3 h-3 text-muted-foreground/60 shrink-0" />}

          </div>

          <span className="text-[12px] text-muted-foreground mt-px block truncate">

            {subtitle}

          </span>

        </div>



        <ChevronRight className="w-4 h-4 text-muted-foreground/30 shrink-0" />

      </div>

    </button>

  );

}



// ── Detail sheet ─────────────────────────────────────

function PlayerDetailSheet({ member, open, onClose, t }) {

  const navigate = useNavigate();

  if (!member) return null;



  const isLeader = LEADER_ROLES.has(member.role);

  const pos = POSITION_MAP[member.position];

  const hasContact = member.email || member.phone;

  // Players are profile identities in Trupp. Chat, however, is account-to-account.
  // Never send a player_profile id to the DM route. Accountless players simply
  // have chat disabled until an account is linked.
  const chatUserId = isLeader
    ? (member.user_id || member.id || null)
    : (member.user_id || member.linked_user_id || null);



  return (

    <Sheet open={open} onOpenChange={onClose}>

      <SheetContent

        side="bottom"

        className="h-auto max-h-[70dvh] rounded-t-2xl bg-card p-0"

        style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}

      >

        <SheetHeader className="px-6 pt-5 pb-0">

          <div className="w-10 h-1 bg-[rgb(var(--tn-tint-ffffff))]/10 rounded-full mx-auto mb-5" />



          <div className="flex items-center gap-4 pb-6">

            <MemberAvatar name={member.name} isLeader={isLeader} size="lg" />

            <div className="min-w-0">

              <SheetTitle className="text-left text-[17px] font-semibold">{member.name}</SheetTitle>

              {isLeader ? (

                <span className="text-[12px] text-muted-foreground/70 mt-0.5 block">

                  {t(getRoleLabelKey(member.role))}

                </span>

              ) : member.position ? (

                <div className="flex items-center gap-2 mt-1">

                  <span

                    className="text-[11px] font-medium px-2 py-0.5 rounded-full"

                    style={{

                      backgroundColor: pos?.bg || "rgb(var(--tn-tint-ffffff) / 0.06)",

                      color: pos?.color || "rgb(var(--tn-text-ffffff) / 0.5)",

                    }}

                  >

                    {member.position}

                  </span>

                  {member.jersey_number && (

                    <span className="text-[11px] text-muted-foreground/50">#{member.jersey_number}</span>

                  )}

                </div>

              ) : member.jersey_number ? (

                <span className="text-[12px] text-muted-foreground/50 mt-0.5 block">

                  #{member.jersey_number}

                </span>

              ) : null}

            </div>

          </div>

        </SheetHeader>



        <div className="px-6 pb-2 space-y-6">

          {/* Actions */}

          <div className="grid grid-cols-3 gap-2.5" data-testid="player-actions">

            <button

              onClick={() => member.phone && (window.location.href = `tel:${member.phone}`)}

              disabled={!member.phone}

              className="flex flex-col items-center gap-1.5 py-4 rounded-xl transition-colors disabled:opacity-30"

              style={{ background: "rgb(var(--tn-tint-ffffff) / 0.04)", border: "1px solid rgb(var(--tn-border-ffffff) / 0.06)" }}

              data-testid="action-call"

            >

              <Phone className="h-5 w-5 text-[rgb(var(--tn-text-4ade80))]" />

              <span className="text-[11px] font-medium text-muted-foreground">{t('roster.detail_call')}</span>

            </button>

            <button

              onClick={() => {
                if (!chatUserId) return;
                navigate(`/chat?dm=${encodeURIComponent(chatUserId)}`);
                onClose();
              }}

              disabled={!chatUserId}

              className="flex flex-col items-center gap-1.5 py-4 rounded-xl transition-colors disabled:opacity-30"

              style={{ background: "rgb(var(--tn-tint-ffffff) / 0.04)", border: "1px solid rgb(var(--tn-border-ffffff) / 0.06)" }}

              data-testid="action-chat"

            >

              <MessageCircle className="h-5 w-5 text-[rgb(var(--tn-text-60a5fa))]" />

              <span className="text-[11px] font-medium text-muted-foreground">{t('roster.detail_chat')}</span>

            </button>

            <button

              onClick={() => member.email && (window.location.href = `mailto:${member.email}`)}

              disabled={!member.email}

              className="flex flex-col items-center gap-1.5 py-4 rounded-xl transition-colors disabled:opacity-30"

              style={{ background: "rgb(var(--tn-tint-ffffff) / 0.04)", border: "1px solid rgb(var(--tn-border-ffffff) / 0.06)" }}

              data-testid="action-email"

            >

              <Mail className="h-5 w-5 text-[rgb(var(--tn-text-fbbf24))]" />

              <span className="text-[11px] font-medium text-muted-foreground">{t('roster.detail_email')}</span>

            </button>

          </div>



          {/* Contact info */}

          {hasContact && (

            <>

              <div className="h-px bg-[rgb(var(--tn-tint-ffffff))]/[0.06]" />

              <div data-testid="player-contact-info">

                <p className="text-[10px] font-semibold text-muted-foreground/50 uppercase tracking-widest mb-3">

                  {t('roster.detail_contact_header')}

                </p>

                <div className="space-y-2">

                  {member.email && (

                    <div className="flex items-center gap-2.5">

                      <Mail className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />

                      <span className="text-[13px] text-muted-foreground/70 truncate">{member.email}</span>

                    </div>

                  )}

                  {member.phone && (

                    <div className="flex items-center gap-2.5">

                      <Phone className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />

                      <span className="text-[13px] text-muted-foreground/70">{member.phone}</span>

                    </div>

                  )}

                </div>

              </div>

            </>

          )}

        </div>

      </SheetContent>

    </Sheet>

  );

}



// ── Sort sheet ───────────────────────────────────────

function SortSheet({ open, onClose, activeSort, onSelect }) {

  const { t } = useTranslation('team_admin');

  return (

    <Sheet open={open} onOpenChange={onClose}>

      <SheetContent

        side="bottom"

        className="h-auto rounded-t-2xl bg-card p-0"

        style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}

      >

        <SheetHeader className="px-6 pt-5 pb-3">

          <div className="w-10 h-1 bg-[rgb(var(--tn-tint-ffffff))]/10 rounded-full mx-auto mb-4" />

          <SheetTitle className="text-left text-base">{t('roster.sort_by')}</SheetTitle>

        </SheetHeader>

        <div className="px-4 pb-4 space-y-0.5" data-testid="sort-options">

          {SORT_OPTIONS.map(opt => (

            <button

              key={opt.value}

              onClick={() => { onSelect(opt.value); onClose(); }}

              className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl transition-colors ${

                activeSort === opt.value

                  ? 'bg-primary/10 text-primary'

                  : "text-[rgb(var(--tn-text-ffffff))]/80 hover:bg-[rgb(var(--tn-tint-ffffff))]/[0.04]"

              }`}

              data-testid={`sort-option-${opt.value}`}

            >

              <span className="text-sm font-medium">{t(opt.key)}</span>

              {activeSort === opt.value && <Check className="w-4 h-4" />}

            </button>

          ))}

        </div>

      </SheetContent>

    </Sheet>

  );

}



// ═══════════════════════════════════════════════════════

// MAIN

// ═══════════════════════════════════════════════════════

export default function SquadMobileView({ embedded = false }) {

  const { t } = useTranslation('team_admin');

  const { user, activeTeam, isCoach } = useAuth();
  const navigate = useNavigate();

  const teamId = activeTeam?.id || user?.active_team_id || user?.team_id;

  const { members, loading } = useTeamMembers(teamId, user?.id);



  const [searchQuery, setSearchQuery] = useState('');

  const [activeSort, setActiveSort] = useState('name');

  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'

  const [filter, setFilter] = useState('all'); // 'all' | 'players' | 'leaders'

  const [sortSheetOpen, setSortSheetOpen] = useState(false);

  const [selectedMember, setSelectedMember] = useState(null);

  const [detailSheetOpen, setDetailSheetOpen] = useState(false);



  // Deduplicate, filter, sort

  const { playerList, leaderList, playerCount, leaderCount, totalCount } = useMemo(() => {

    // Deduplicate by id, skip members without names

    const seen = new Set();

    const unique = [];

    for (const m of members) {

      if (!seen.has(m.id) && m.name?.trim()) {

        seen.add(m.id);

        unique.push(m);

      }

    }



    let filtered = unique;

    if (searchQuery) {

      const q = searchQuery.toLowerCase();

      filtered = unique.filter(m =>

        m.name?.toLowerCase().includes(q) ||

        m.position?.toLowerCase().includes(q) ||

        m.jersey_number?.toString().includes(q)

      );

    }



    const pList = filtered.filter(m => m.role === 'player');

    const lList = filtered.filter(m => LEADER_ROLES.has(m.role));



    const cmp = (a, b) => {

      let res;

      switch (activeSort) {

        case 'position': {

          const pa = POSITION_SORT_ORDER[a.position] || 99;

          const pb = POSITION_SORT_ORDER[b.position] || 99;

          res = pa !== pb ? pa - pb : (a.name || '').localeCompare(b.name || '', 'sv');

          break;

        }

        case 'number': {

          const na = parseInt(a.jersey_number) || 999;

          const nb = parseInt(b.jersey_number) || 999;

          res = na !== nb ? na - nb : (a.name || '').localeCompare(b.name || '', 'sv');

          break;

        }

        case 'name':

        default:

          res = (a.name || '').localeCompare(b.name || '', 'sv');

      }

      return sortDir === 'desc' ? -res : res;

    };

    pList.sort(cmp);

    lList.sort((a, b) => {

      const res = (a.name || '').localeCompare(b.name || '', 'sv');

      return sortDir === 'desc' ? -res : res;

    });



    const totPlayers = unique.filter(m => m.role === 'player').length;

    const totLeaders = unique.filter(m => LEADER_ROLES.has(m.role)).length;

    return {

      playerList: pList,

      leaderList: lList,

      playerCount: totPlayers,

      leaderCount: totLeaders,

      totalCount: totPlayers + totLeaders,

    };

  }, [members, searchQuery, activeSort, sortDir]);



  const activeSortLabel = t(SORT_OPTIONS.find(s => s.value === activeSort)?.key || 'roster.sort_name');

  const openMember = (m) => {
    if (isCoach && m.role === 'player') {
      navigate(`/players/${encodeURIComponent(m.player_profile_id || m.id)}`);
      return;
    }
    setSelectedMember(m);
    setDetailSheetOpen(true);
  };



  if (loading) {

    return (

      <div className="p-4 space-y-4" style={{ paddingTop: 16 }}>

        <Skeleton className="h-7 w-24" />

        <Skeleton className="h-11 w-full rounded-xl" />

        <div className="space-y-2.5 mt-2">

          {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-[60px] w-full rounded-2xl" />)}

        </div>

      </div>

    );

  }



  // Apply role filter

  const showPlayers = filter === 'all' || filter === 'players';

  const showLeaders = filter === 'all' || filter === 'leaders';

  const visiblePlayers = showPlayers ? playerList : [];

  const visibleLeaders = showLeaders ? leaderList : [];

  const isEmpty = visiblePlayers.length === 0 && visibleLeaders.length === 0;



  // Header count badge follows filter selection

  const headerCount = filter === 'leaders' ? leaderCount : filter === 'players' ? playerCount : totalCount;

  const headerCountLabel = filter === 'leaders'

    ? t('roster.count_leaders', { count: leaderCount, defaultValue: `${leaderCount} ${leaderCount === 1 ? 'leader' : 'leaders'}` })

    : filter === 'players'

    ? t('roster.count_players', { count: playerCount, defaultValue: `${playerCount} ${playerCount === 1 ? 'player' : 'players'}` })

    : t('roster.count_people', { count: headerCount, defaultValue: `${headerCount} people` });



  return (

    <div

      className={embedded ? "tn-player-roster" : "p-4"}

      style={{

        paddingTop: 16,

        paddingBottom: 16,

      }}

      data-testid="squad-mobile-view"

    >

      {/* Header */}

      <div className="flex items-center justify-between mb-4">

        {!embedded && <h1 className="text-[22px] font-bold text-[rgb(var(--tn-text-ffffff))]">{t('page_title', { defaultValue: 'Squad' })}</h1>}

        <span className="text-[12px] text-muted-foreground/70" data-testid="squad-count-badge">

          {headerCountLabel}

        </span>

      </div>



      {/* Search */}

      <div className="relative mb-3">

        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/50" />

        <Input

          placeholder={t('roster.search_placeholder')}

          value={searchQuery}

          onChange={(e) => setSearchQuery(e.target.value)}

          className="pl-10 pr-10 h-11 rounded-xl bg-[rgb(var(--tn-tint-ffffff))]/[0.04] border-[rgb(var(--tn-border-ffffff))]/[0.06] text-sm placeholder:text-muted-foreground/40"

          data-testid="search-input"

        />

        {searchQuery && (

          <button onClick={() => setSearchQuery('')} className="absolute right-3.5 top-1/2 -translate-y-1/2" data-testid="clear-search">

            <X className="h-4 w-4 text-muted-foreground/50" />

          </button>

        )}

      </div>



      {/* Filter chips */}

      <div className="flex items-center gap-2 mb-3 overflow-x-auto -mx-1 px-1 no-scrollbar" data-testid="squad-filter-chips">

        <FilterChip active={filter === 'all'} onClick={() => setFilter('all')} label={t('roster.filter_all')} count={totalCount} testid="squad-filter-all" />

        <FilterChip active={filter === 'players'} onClick={() => setFilter('players')} label={t('roster.filter_players')} count={playerCount} testid="squad-filter-players" />

        <FilterChip active={filter === 'leaders'} onClick={() => setFilter('leaders')} label={t('roster.filter_leaders')} count={leaderCount} testid="squad-filter-leaders" />

      </div>



      {/* Sort controls */}

      <div className="flex items-center gap-2 mb-4">

        <button

          onClick={() => setSortSheetOpen(true)}

          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium text-muted-foreground/70 hover:text-[rgb(var(--tn-text-ffffff))]/85 transition-colors"

          style={{ background: "rgb(var(--tn-tint-ffffff) / 0.03)", border: "1px solid rgb(var(--tn-border-ffffff) / 0.05)" }}

          data-testid="sort-button"

        >

          <ArrowUpDown className="w-3 h-3" />

          {t('roster.sort_label', { value: activeSortLabel })}

        </button>

        <button

          onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}

          className="px-3 py-1.5 rounded-full text-[11px] font-medium text-muted-foreground/70 hover:text-[rgb(var(--tn-text-ffffff))]/85 transition-colors tabular-nums"

          style={{ background: "rgb(var(--tn-tint-ffffff) / 0.03)", border: "1px solid rgb(var(--tn-border-ffffff) / 0.05)" }}

          data-testid="sort-direction"

          aria-label={t('roster.sort_direction_label')}

        >

          {sortDir === 'asc' ? t('roster.sort_direction_asc') : t('roster.sort_direction_desc')}

        </button>

      </div>



      {/* List */}

      {isEmpty ? (

        <div className="text-center py-16">

          <Users className="h-10 w-10 mx-auto mb-3 text-muted-foreground/20" />

          <p className="text-sm text-muted-foreground/50">

            {searchQuery ? t('roster.empty_no_match') : t('roster.empty_no_members')}

          </p>

        </div>

      ) : (

        <div className="space-y-5">

          {visiblePlayers.length > 0 && (

            <div>

              {filter === 'all' && (

                <SubsectionHeader label={t('roster.filter_players')} count={visiblePlayers.length} />

              )}

              <div className="space-y-1.5">

                {visiblePlayers.map(m => (

                  <PlayerRow key={m.id} member={m} onClick={openMember} isLeader={false} t={t} />

                ))}

              </div>

            </div>

          )}

          {visibleLeaders.length > 0 && (

            <div>

              {filter === 'all' && (

                <SubsectionHeader label={t('roster.filter_leaders')} count={visibleLeaders.length} />

              )}

              <div className="space-y-1.5">

                {visibleLeaders.map(m => (

                  <PlayerRow key={m.id} member={m} onClick={openMember} isLeader t={t} />

                ))}

              </div>

            </div>

          )}

        </div>

      )}



      <SortSheet open={sortSheetOpen} onClose={() => setSortSheetOpen(false)} activeSort={activeSort} onSelect={setActiveSort} />

      <PlayerDetailSheet member={selectedMember} open={detailSheetOpen} onClose={() => setDetailSheetOpen(false)} t={t} />

    </div>

  );

}



function SubsectionHeader({ label, count }) {

  return (

    <div className="px-1 mb-2 flex items-center gap-1.5">

      <span className="text-[10px] font-semibold text-[rgb(var(--tn-text-ffffff))]/40 uppercase tracking-[0.12em]">

        {label}

      </span>

      {count != null && (

        <span className="text-[10px] text-[rgb(var(--tn-text-ffffff))]/30">({count})</span>

      )}

    </div>

  );

}



function FilterChip({ active, onClick, label, count, testid }) {

  return (

    <button

      onClick={onClick}

      className={cn(

        'px-3.5 py-1.5 rounded-full text-[12px] font-medium transition-colors shrink-0 border',

        active

          ? "bg-amber-500/15 text-[rgb(var(--tn-text-fde68a))] border-amber-500/30"

          : "bg-[rgb(var(--tn-tint-ffffff))]/[0.03] text-[rgb(var(--tn-text-ffffff))]/65 border-[rgb(var(--tn-border-ffffff))]/[0.06] hover:text-[rgb(var(--tn-text-ffffff))]/85"

      )}

      data-testid={testid}

    >

      {label}

      {count != null && <span className="ml-1.5 text-[rgb(var(--tn-text-ffffff))]/40">{count}</span>}

    </button>

  );

}
